import { type RowNode } from "ag-grid-community";
import { concat, last, remove } from "ramda";
import { getFilenameInfo, splitPath } from "widgets/utils";

import { getComparatorByCellType } from "@/app-utils/grid.js";
import { compareAlphaNumerically } from "@/app-utils/sort.js";
import type { ProjectFile } from "@/graphql.js";
import { IndexStatus } from "@/graphql.js";
import type { BrowsingDirectoryFragment, BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql";
import type { GetFileInfoCardQuery } from "@/modules/files/graphql/files-queries.gql";
import { isBedFile, isGenomeViewerFile, isIndexRequiredForGenomeViewer } from "@/modules/files/utils/file";
import { FILE_BROWSE_TYPES } from "@/settings/settings.js";

/**
 * Sort files and folders with a special logic.
 *   a) first order by file type keep all folders before files
 *   b) then order by file name, but embedded numbers are handled special
 *       eg: a2 < a10, natural compare by letters would return a10 < a2
 * @param fileA
 * @param fileB
 */
export function defaultFileBrowserSorting(
  fileA: BrowsingProjectFileFragment,
  fileB: BrowsingProjectFileFragment,
): number {
  return compareByFileType(fileA, fileB) || compareFS(fileA.fileName, fileB.fileName);
}

// the default comparison in FileBrowser
export function compareFS(a: string, b: string): number {
  return compareAlphaNumerically(a, b, false, true);
}

export function compareByFileType(fileA: BrowsingProjectFileFragment, fileB: BrowsingProjectFileFragment) {
  if (fileA?.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY && fileB?.fileBrowseType !== FILE_BROWSE_TYPES.DIRECTORY) {
    return -1;
  } else if (
    fileA?.fileBrowseType !== FILE_BROWSE_TYPES.DIRECTORY &&
    fileB?.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY
  ) {
    return 1;
  }
  return 0;
}

// Allow sorting by file and then by any other comparison
export function fileTypeComparator(cellType: string) {
  return (
    valueA: string,
    valueB: string,
    nodeA: { data: { file: BrowsingProjectFileFragment } },
    nodeB: { data: { file: BrowsingProjectFileFragment } },
    isInverted: boolean,
  ) => {
    return (
      compareByFileType(nodeA.data.file, nodeB.data.file) ||
      getComparatorByCellType(cellType)(valueA, valueB, nodeA as RowNode, nodeB as RowNode, isInverted)
    );
  };
}

// allow to sort by file and then by any other comparison
export function fileNameComparator(cellType: string) {
  return (
    valueA: BrowsingProjectFileFragment,
    valueB: BrowsingProjectFileFragment,
    nodeA: { data: { file: BrowsingProjectFileFragment } },
    nodeB: { data: { file: BrowsingProjectFileFragment } },
    isInverted: boolean,
  ) => {
    return (
      compareByFileType(nodeA.data.file, nodeB.data.file) ||
      getComparatorByCellType(cellType)(
        valueA.fileName,
        valueB.fileName,
        nodeA as RowNode,
        nodeB as RowNode,
        isInverted,
      )
      // (compareFS(valueA, valueB)  * (isInverted ? -1 : 1))
    );
  };
}

export interface Breadcrumb {
  id: number;
  humanReadablePath: string;
}

export function buildBreadcrumbProjectFileData(folder: BrowsingDirectoryFragment): Breadcrumb[] {
  if (!folder) {
    return [];
  }

  const decodedPaths: string[] = splitPath(folder.humanReadablePath!);
  const allIds = concat(folder.parentFolderIds ?? [], [folder.id]);

  return allIds?.map(
    (id, index) =>
      ({
        id,
        humanReadablePath: decodedPaths.slice(0, index + 1).join("/"),
      }) as unknown as Breadcrumb,
  );
  // We cast here because the graphql api return any for Long as this a Java Object instead of a real Number
}

export const generateBreadcrumbs = (folder: BrowsingDirectoryFragment): Breadcrumb[] => {
  const allBreadcrumbs = buildBreadcrumbProjectFileData(folder);
  // Remove User Group level
  return remove(0, 1, allBreadcrumbs);
};

export const getProjectFileIdFromFolder = (folder: BrowsingDirectoryFragment | undefined | null): number | null =>
  folder?.parentFolderIds?.[1] ?? folder?.id ?? null; // because [0] -> User Group

export const getFolderFromFile = (
  file: BrowsingDirectoryFragment | BrowsingProjectFileFragment | undefined | null,
): number | null => {
  if (!file) return null;

  if (file.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY) {
    return file.id;
  } else {
    return last(file.parentFolderIds);
  }
};

export const getFileType = (file: GetFileInfoCardQuery["projectFileById"]) => {
  const fileInfo = getFilenameInfo(file.fileName);

  return file.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY
    ? "Folder"
    : file.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK
      ? "Link"
      : (fileInfo?.typeLabel ?? "Unknown file");
};

export const getFilesApplicableToGenomeViewer = (files): ProjectFile[] => {
  if (!files) {
    return [];
  }
  return files
    .filter((file) => file && isGenomeViewerFile(file))
    .filter(
      (file) => isBedFile(file) || (isIndexRequiredForGenomeViewer(file) && file.indexingStatus == IndexStatus.Exists),
    );
};
